import { useRuntimeConfig } from '#app'
import { useMainStore } from '@/composables/store'
import {
  captureRedirectTrackingParamsFromUrl,
  FBCID_STORAGE_KEY,
  FBCLID_STORAGE_KEY,
  getRedirectAwareHost,
  getRedirectTrackingQuery,
} from '@/utils/eventTracking'
import { getCurrentServer, getToken } from '@/utils/globalData'
import {
  getOnlineVisitorId,
  resolveOnlineVisitShopId,
} from '@/utils/onlineVisit'
import { getOrderTrackingAliases, resolveOrderTrackingKey } from '@/utils/orderTrackingIdentity'

const CLIENT_EVENT_LOG_ENDPOINT = '/api/mall/frontserver/clientEventLog/record'
const ATTRIBUTION_STORAGE_KEY = 'mall_web_attribution_context_v1'
const QUEUE_STORAGE_KEY = 'mall_client_event_log_queue_v1'
const ACK_STORAGE_KEY = 'mall_client_event_log_ack_v1'
const SESSION_STORAGE_KEY = 'mall_client_event_log_session_id_v1'
const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const MAX_QUEUE_SIZE = 200
const MAX_ACK_SIZE = 200
const REQUEST_TIMEOUT_MS = 8000
const RETRY_BASE_MS = 1500
const RETRY_MAX_MS = 5 * 60 * 1000
const ORDER_SUCCESS_CODE_PREFIX = 'SO'

const AD_QUERY_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term',
  'fbclid',
  'fbcid',
  'ttclid',
  'campaign',
  'campaign_id',
  'campaignid',
  'fb_campaign_id',
  'tt_campaign_id',
  'adset',
  'adset_id',
  'adsetid',
  'adset_name',
  'adgroup_id',
  'adgroupid',
  'adgroup_name',
  'tt_adgroup_id',
  'ad',
  'ad_id',
  'adid',
  'ad_name',
  'tt_ad_id',
  'placement',
  'source',
])

const FUNNEL_EVENTS = Object.freeze({
  login_success: { module: 'auth', source: 'login_result', itemType: 'account' },
  registration_success: { module: 'auth', source: 'registration_result', itemType: 'account' },
  view_content: { module: 'product', source: 'product_detail', itemType: 'product' },
  add_to_cart: { module: 'cart', source: 'cart_action', itemType: 'product' },
  view_cart: { module: 'cart', source: 'cart_view', itemType: 'cart' },
  initiate_checkout: { module: 'checkout', source: 'checkout_start', itemType: 'cart' },
})

let initialized = false
let queueCache = null
let ackCache = null
let flushPromise = null
let flushTimer = null

const isClientRuntime = () => typeof window !== 'undefined' && typeof document !== 'undefined'
const normalizeText = (value, maxLength = 255) => String(value ?? '').trim().slice(0, maxLength)

function firstNonEmpty(...values) {
  for (const value of values) {
    const normalizedValue = normalizeText(value, 1000)
    if (normalizedValue)
      return normalizedValue
  }
  return ''
}

function getClientAppVersion() {
  try {
    return normalizeText(useRuntimeConfig()?.public?.frontendErrorLogAppVersion, 50)
  }
  catch {
    return ''
  }
}

function getClientBuildNumber(appVersion) {
  return normalizeText(appVersion, 50).match(/^site_(\d+)$/i)?.[1] || ''
}

export function isValidOrderSuccessCode(value) {
  return normalizeText(value, 1000).toUpperCase().startsWith(ORDER_SUCCESS_CODE_PREFIX)
}

// orderId/dealerOrderPrimaryId are internal numeric identifiers. They are
// useful for matching an order detail response, but they are not a reliable
// order-success conversion key when the success page has not loaded the
// actual order code yet.
function resolveOrderSuccessCode(order = {}) {
  return [order.orderCode, order.orderNumber]
    .map(value => firstNonEmpty(value))
    .find(isValidOrderSuccessCode) || ''
}

function runSafely(callback, fallback = null) {
  try {
    return callback()
  }
  catch {
    return fallback
  }
}

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value)
  }
  catch {
    return fallback
  }
}

function safeStorageGet(storage, key) {
  if (!isClientRuntime())
    return ''
  try {
    return storage?.getItem(key) || ''
  }
  catch {
    return ''
  }
}

function safeStorageSet(storage, key, value) {
  if (!isClientRuntime())
    return false
  try {
    storage?.setItem(key, value)
    return true
  }
  catch {
    return false
  }
}

function randomId(prefix = 'web') {
  if (isClientRuntime() && window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function getSessionId() {
  const storedSessionId = normalizeText(
    safeStorageGet(window.sessionStorage, SESSION_STORAGE_KEY),
    100,
  )
  if (storedSessionId)
    return storedSessionId

  const sessionId = randomId('web_session').slice(0, 100)
  safeStorageSet(window.sessionStorage, SESSION_STORAGE_KEY, sessionId)
  return sessionId
}

function getMainStoreSafely() {
  try {
    return useMainStore()
  }
  catch {
    return null
  }
}

function getSafeReferrer() {
  if (!isClientRuntime() || !document.referrer)
    return ''
  try {
    const url = new URL(document.referrer)
    return `${url.origin}${url.pathname}`.slice(0, 1000)
  }
  catch {
    return ''
  }
}

function getReferrerHost() {
  const referrer = getSafeReferrer()
  if (!referrer)
    return ''
  try {
    return normalizeText(new URL(referrer).hostname, 255).toLowerCase()
  }
  catch {
    return ''
  }
}

function filterAdQuery(query = {}) {
  return AD_QUERY_KEYS.reduce((result, key) => {
    const value = normalizeText(query[key], 500)
    if (value)
      result[key] = value
    return result
  }, {})
}

function collectAdQuery(inputUrl) {
  if (!isClientRuntime())
    return {}

  try {
    const currentUrl = inputUrl instanceof URL
      ? inputUrl
      : new URL(inputUrl || window.location.href, window.location.origin)
    captureRedirectTrackingParamsFromUrl(currentUrl)
    const storedQuery = getRedirectTrackingQuery()
    const currentQuery = {}
    currentUrl.searchParams.forEach((value, key) => {
      currentQuery[key] = value
    })
    return filterAdQuery({
      ...storedQuery,
      ...currentQuery,
    })
  }
  catch {
    return {}
  }
}

function inferAttributionSource(query = {}, referrerHost = '') {
  const sourceText = firstNonEmpty(query.utm_source, query.source).toLowerCase()
  const referrer = normalizeText(referrerHost, 255).toLowerCase()
  const currentHost = normalizeText(window.location.hostname, 255).toLowerCase()
  const redirectAwareHost = normalizeText(getRedirectAwareHost(), 255).toLowerCase()

  if (
    query.ttclid
    || query.tt_campaign_id
    || query.tt_adgroup_id
    || query.tt_ad_id
    || /(?:^|[^a-z])(?:tiktok|tik_tok|tt)(?:[^a-z]|$)/i.test(sourceText)
    || /(?:^|\.)tiktok\.com$/i.test(referrer)
  ) {
    return 'tiktok'
  }

  if (
    query.fbclid
    || query.fbcid
    || query.fb_campaign_id
    || /(?:^|[^a-z])(?:facebook|meta|instagram|fb|ig)(?:[^a-z]|$)/i.test(sourceText)
    || /(?:^|\.)(?:facebook|instagram)\.com$/i.test(referrer)
  ) {
    return 'meta'
  }

  if (query.utm_source || query.utm_campaign || query.utm_id)
    return 'utm'
  if (referrer && referrer !== currentHost && referrer !== redirectAwareHost)
    return 'referral'
  return 'direct'
}

function buildSafeLandingUrl(query = {}) {
  if (!isClientRuntime())
    return ''
  try {
    const host = getRedirectAwareHost() || window.location.host
    const url = new URL(window.location.href)
    if (host)
      url.host = host
    url.search = ''
    Object.entries(filterAdQuery(query)).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })
    url.hash = ''
    return url.toString().slice(0, 3000)
  }
  catch {
    return ''
  }
}

function buildAttributionTouch(inputUrl) {
  const query = collectAdQuery(inputUrl)
  const referrerHost = getReferrerHost()
  const source = inferAttributionSource(query, referrerHost)
  const campaignId = firstNonEmpty(
    query.campaign_id,
    query.campaignid,
    query.fb_campaign_id,
    query.tt_campaign_id,
    query.utm_id,
  )
  const adSetId = firstNonEmpty(
    query.adset_id,
    query.adsetid,
    query.adgroup_id,
    query.adgroupid,
    query.tt_adgroup_id,
    query.utm_term,
  )
  const adId = firstNonEmpty(
    query.ad_id,
    query.adid,
    query.tt_ad_id,
    query.utm_content,
  )

  return {
    source,
    isAttributed: source !== 'direct' && source !== 'referral',
    mediaSource: firstNonEmpty(query.utm_source, query.source, source),
    medium: normalizeText(query.utm_medium, 255),
    campaign: firstNonEmpty(query.utm_campaign, query.campaign, campaignId),
    campaignId,
    adSetId,
    adSetName: firstNonEmpty(query.adset_name, query.adgroup_name, query.adset),
    adId,
    adName: firstNonEmpty(query.ad_name, query.ad),
    placement: normalizeText(query.placement, 255),
    fbclid: firstNonEmpty(query.fbclid, query.fbcid),
    ttclid: normalizeText(query.ttclid, 500),
    referrerHost,
    landingUrl: buildSafeLandingUrl(query),
    query,
    capturedAt: new Date().toISOString(),
  }
}

function isStoredAttributionFresh(context) {
  return context
    && Number(context.updatedAt) > 0
    && Date.now() - Number(context.updatedAt) <= ATTRIBUTION_MAX_AGE_MS
}

function readStoredAttribution() {
  const parsed = safeJsonParse(
    safeStorageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY),
    null,
  )
  return isStoredAttributionFresh(parsed) ? parsed : null
}

export function captureWebAttribution(inputUrl) {
  if (!isClientRuntime())
    return null

  const currentTouch = buildAttributionTouch(inputUrl)
  const storedContext = readStoredAttribution()
  const hasNewAttributionSignal = currentTouch.isAttributed
    || currentTouch.source === 'referral'
    || Boolean(currentTouch.fbclid || currentTouch.ttclid)
  const firstTouch = storedContext?.firstTouch || currentTouch
  const lastTouch = hasNewAttributionSignal
    ? currentTouch
    : (storedContext?.lastTouch || currentTouch)
  const context = {
    firstTouch,
    lastTouch,
    currentTouch,
    updatedAt: Date.now(),
  }

  safeStorageSet(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(context))
  return context
}

export function getWebAttributionContext() {
  if (!isClientRuntime())
    return null
  return captureWebAttribution() || readStoredAttribution()
}

function getResolvedAttribution() {
  const context = getWebAttributionContext()
  const currentTouch = context?.currentTouch || {}
  const lastTouch = context?.lastTouch || {}
  const resolvedTouch = currentTouch.isAttributed ? currentTouch : lastTouch

  return {
    context,
    touch: resolvedTouch || {},
    isAttributed: Boolean(resolvedTouch?.isAttributed),
  }
}

function getCookieValueForDiagnostics(key) {
  if (!isClientRuntime())
    return ''

  try {
    const encodedKey = encodeURIComponent(normalizeText(key, 100))
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodedKey}=([^;]*)`))
    return match?.[1] ? normalizeText(decodeURIComponent(match[1]), 1000) : ''
  }
  catch {
    return ''
  }
}

function buildFacebookEventSourceUrlForDiagnostics() {
  if (!isClientRuntime())
    return ''

  try {
    const locationHref = window.location.href
    const joinParts = []
    const appendStoredValue = (key) => {
      if (locationHref.includes(key))
        return
      const value = safeStorageGet(window.localStorage, key)
      if (value)
        joinParts.push(`${key}=${value}`)
    }

    appendStoredValue('redirectSubDomain')
    appendStoredValue(FBCID_STORAGE_KEY)
    appendStoredValue(FBCLID_STORAGE_KEY)

    if (!joinParts.length)
      return locationHref.slice(0, 3000)

    const separator = locationHref.includes('?') ? '&' : '?'
    return `${locationHref}${separator}${joinParts.join('&')}`.slice(0, 3000)
  }
  catch {
    return ''
  }
}

function buildAttributionDiagnostics({ context, touch, purchaseEventId, metaTrackingSnapshot } = {}) {
  if (!isClientRuntime())
    return {}

  try {
    const currentUrl = new URL(window.location.href)
    const pageFbclid = normalizeText(currentUrl.searchParams.get(FBCLID_STORAGE_KEY), 1000)
    const pageFbcid = normalizeText(currentUrl.searchParams.get(FBCID_STORAGE_KEY), 1000)
    const localStorageFbclid = safeStorageGet(window.localStorage, FBCLID_STORAGE_KEY)
    const localStorageFbcid = safeStorageGet(window.localStorage, FBCID_STORAGE_KEY)
    const metaFbclid = normalizeText(
      metaTrackingSnapshot?.fbclid,
      1000,
    )
    const metaFbc = normalizeText(
      metaTrackingSnapshot?.fbc || getCookieValueForDiagnostics('_fbc'),
      1000,
    )
    const metaFbp = normalizeText(
      metaTrackingSnapshot?.fbp || getCookieValueForDiagnostics('_fbp'),
      1000,
    )
    const resolvedFbclid = normalizeText(touch?.fbclid, 1000)
    const storedRedirectQuery = runSafely(() => getRedirectTrackingQuery(), {}) || {}
    const metaFbcMatchesPage = Boolean(
      pageFbclid
      && metaFbc
      && metaFbc.endsWith(`.${pageFbclid}`),
    )
    const metaFbcMatchesResolved = Boolean(
      resolvedFbclid
      && metaFbc
      && metaFbc.endsWith(`.${resolvedFbclid}`),
    )
    const metaFbcStatus = !metaFbc
      ? 'missing'
      : (metaFbcMatchesPage || metaFbcMatchesResolved ? 'matches_current_touch' : 'does_not_match_current_touch')

    return {
      version: 1,
      capturedAt: new Date().toISOString(),
      purchaseEventId: normalizeText(purchaseEventId, 255),
      pageUrl: currentUrl.toString().slice(0, 3000),
      facebookEventSourceUrl: buildFacebookEventSourceUrlForDiagnostics(),
      referrer: getSafeReferrer(),
      pageFbclid,
      pageFbcid,
      localStorageFbclid,
      localStorageFbcid,
      storedRedirectQuery,
      metaFbc,
      metaFbp,
      metaFbclid,
      metaFbcStatus,
      firstTouchFbclid: normalizeText(context?.firstTouch?.fbclid, 1000),
      lastTouchFbclid: normalizeText(context?.lastTouch?.fbclid, 1000),
      currentTouchFbclid: normalizeText(context?.currentTouch?.fbclid, 1000),
      resolvedTouchFbclid: resolvedFbclid,
      consistency: {
        pageMatchesResolvedTouch: Boolean(pageFbclid && resolvedFbclid && pageFbclid === resolvedFbclid),
        localStorageMatchesPage: Boolean(pageFbclid && localStorageFbclid && pageFbclid === localStorageFbclid),
        metaFbcMatchesPage,
        metaFbcMatchesResolved,
      },
    }
  }
  catch {
    return {}
  }
}

function getPlatformType() {
  if (!isClientRuntime())
    return 'pc-web'
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '')
    ? 'h5'
    : 'pc-web'
}

function getDeviceType() {
  if (!isClientRuntime())
    return ''
  const ua = navigator.userAgent || ''
  if (/ipad|tablet/i.test(ua))
    return 'tablet'
  if (/android|iphone|ipod|mobile/i.test(ua))
    return 'mobile'
  return 'desktop'
}

function getUserContext() {
  const store = getMainStoreSafely()
  const user = store?.mallUserInfo || store?.userInfo || {}
  const userId = firstNonEmpty(user.id, user.userId, user.customerInfoId)
  const memberCode = firstNonEmpty(user.memberCode, user.code, userId)

  return {
    userId,
    memberCode,
    userName: firstNonEmpty(user.userName, user.username, user.account),
    nickname: firstNonEmpty(user.nickname, user.nickName),
    memberName: firstNonEmpty(user.memberName, user.name),
    displayName: firstNonEmpty(user.displayName, user.nickname, user.name),
    isLogin: Boolean(userId || getToken()),
  }
}

function getSiteContext() {
  const store = getMainStoreSafely()
  const siteInfo = typeof store?.getSiteInfo === 'function'
    ? (store.getSiteInfo() || {})
    : (store?.siteInfo || {})
  const domain = getRedirectAwareHost() || window.location.host
  const shopId = resolveOnlineVisitShopId()

  return {
    domain: normalizeText(domain, 255),
    shopId: normalizeText(shopId, 100),
    siteName: firstNonEmpty(
      siteInfo.pc_SITE_NAME,
      siteInfo.mobile_SITE_NAME,
      siteInfo.title,
    ),
    appType: firstNonEmpty(siteInfo.siteCode, domain, 'mall-web').slice(0, 100),
  }
}

function buildBasePayload({ eventName, eventModule, eventSource, recordKey }) {
  const user = getUserContext()
  const site = getSiteContext()
  const visitorId = getOnlineVisitorId()
  const sessionId = getSessionId()
  const attribution = getResolvedAttribution()
  const pageRoute = `${window.location.pathname || '/'}${window.location.hash || ''}`
  const appVersion = getClientAppVersion()
  const buildNumber = getClientBuildNumber(appVersion)

  return {
    recordKey,
    clientType: 'pc',
    appType: site.appType,
    platformType: getPlatformType(),
    appVersion: appVersion || undefined,
    buildNumber: buildNumber || undefined,
    channel: firstNonEmpty(attribution.touch.source, 'direct'),
    memberCode: user.memberCode || `guest:${visitorId}`,
    userId: user.userId || undefined,
    userName: user.userName || undefined,
    nickname: user.nickname || undefined,
    memberName: user.memberName || undefined,
    displayName: user.displayName || undefined,
    isLogin: user.isLogin,
    sessionId,
    deviceNo: visitorId,
    deviceType: getDeviceType(),
    userAgent: navigator.userAgent || '',
    currentServer: firstNonEmpty(getCurrentServer(), window.location.origin),
    eventName,
    eventModule,
    eventSource,
    eventStatus: 'success',
    pageRoute: normalizeText(pageRoute, 255),
    occurredAt: new Date().toISOString(),
    itemId: recordKey,
    isAttributed: attribution.isAttributed,
    attributionSource: attribution.touch.source || 'direct',
    campaign: firstNonEmpty(attribution.touch.campaign, attribution.touch.campaignId),
    mediaSource: firstNonEmpty(attribution.touch.mediaSource, attribution.touch.source),
    deepLinkValue: normalizeText(attribution.touch.landingUrl, 255),
    oneLinkUrl: normalizeText(attribution.touch.landingUrl, 3000),
    payload: {
      clientEventId: recordKey,
      domain: site.domain,
      shopId: site.shopId,
      siteName: site.siteName,
      appVersion,
      buildNumber,
      referrer: getSafeReferrer(),
      attribution: attribution.context,
    },
    tags: {
      clientEventId: recordKey,
      appVersion,
      buildNumber,
      attributionSource: attribution.touch.source || 'direct',
      campaignId: attribution.touch.campaignId || '',
      adSetId: attribution.touch.adSetId || '',
      adSetName: attribution.touch.adSetName || '',
      adId: attribution.touch.adId || '',
      adName: attribution.touch.adName || '',
      placement: attribution.touch.placement || '',
      fbclid: attribution.touch.fbclid || '',
      ttclid: attribution.touch.ttclid || '',
    },
  }
}

function pruneQueue(queue) {
  return (Array.isArray(queue) ? queue : [])
    .filter(item => item?.recordKey && item?.payload && Date.now() - Number(item.createdAt) <= QUEUE_MAX_AGE_MS)
    .slice(-MAX_QUEUE_SIZE)
}

function readQueue() {
  if (queueCache)
    return queueCache
  queueCache = pruneQueue(safeJsonParse(
    safeStorageGet(window.localStorage, QUEUE_STORAGE_KEY),
    [],
  ))
  return queueCache
}

function writeQueue(queue) {
  queueCache = pruneQueue(queue)
  safeStorageSet(window.localStorage, QUEUE_STORAGE_KEY, JSON.stringify(queueCache))
}

function readAckKeys() {
  if (ackCache)
    return ackCache
  const parsed = safeJsonParse(safeStorageGet(window.localStorage, ACK_STORAGE_KEY), [])
  ackCache = Array.isArray(parsed) ? parsed.slice(-MAX_ACK_SIZE) : []
  return ackCache
}

function markAcknowledged(recordKey) {
  ackCache = [
    ...readAckKeys().filter(item => item !== recordKey),
    recordKey,
  ].slice(-MAX_ACK_SIZE)
  safeStorageSet(window.localStorage, ACK_STORAGE_KEY, JSON.stringify(ackCache))
}

function scheduleFlush(delay = 0) {
  if (!isClientRuntime())
    return
  if (flushTimer)
    window.clearTimeout(flushTimer)
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    void flushClientEventLogQueue()
  }, Math.max(0, delay))
}

function enqueuePayload(payload) {
  if (!isClientRuntime() || !payload?.recordKey)
    return false
  if (readAckKeys().includes(payload.recordKey))
    return false

  const queue = readQueue()
  if (queue.some(item => item.recordKey === payload.recordKey)) {
    scheduleFlush(100)
    return false
  }

  queue.push({
    recordKey: payload.recordKey,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
    nextRetryAt: Date.now(),
  })
  writeQueue(queue)
  scheduleFlush(0)
  return true
}

function bumpRetry(recordKey) {
  const queue = readQueue()
  let nextRetryAt = Date.now() + RETRY_MAX_MS
  const nextQueue = queue.map((item) => {
    if (item.recordKey !== recordKey)
      return item
    const retryCount = Number(item.retryCount || 0) + 1
    const delay = Math.min(RETRY_BASE_MS * (2 ** Math.min(retryCount - 1, 8)), RETRY_MAX_MS)
    nextRetryAt = Date.now() + delay
    return {
      ...item,
      retryCount,
      nextRetryAt,
    }
  })
  writeQueue(nextQueue)
  scheduleFlush(Math.max(RETRY_BASE_MS, nextRetryAt - Date.now()))
}

async function sendQueueItem(item, options = {}) {
  // Revalidate at the final network boundary as well, so an invalid
  // order_success left in localStorage by an older build cannot be retried.
  if (item?.payload?.eventName === 'order_success' && !isValidOrderSuccessCode(item.payload.orderCode)) {
    reportBlockedOrderSuccessInternal({
      ...item.payload,
      ...(item.payload.payload || {}),
      source: 'client_event_log_queue_guard',
    })
    return true
  }

  const controller = typeof AbortController !== 'undefined' && !options.keepalive
    ? new AbortController()
    : null
  const timer = controller
    ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null

  try {
    const response = await fetch(CLIENT_EVENT_LOG_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item.payload),
      keepalive: Boolean(options.keepalive),
      signal: controller?.signal,
    })
    if (!response.ok)
      throw new Error(`client event log request failed: ${response.status}`)
    const result = await response.json().catch(() => ({ success: true }))
    if (result?.success === false)
      throw new Error(result.message || 'client event log request failed')
    return true
  }
  finally {
    if (timer)
      window.clearTimeout(timer)
  }
}

export function flushClientEventLogQueue(options = {}) {
  if (!isClientRuntime() || navigator.onLine === false)
    return Promise.resolve(false)
  if (flushPromise)
    return flushPromise

  flushPromise = (async () => {
    const readyItems = readQueue()
      .filter(item => Number(item.nextRetryAt || 0) <= Date.now())
      .slice(0, 10)

    for (const item of readyItems) {
      try {
        await sendQueueItem(item, options)
        writeQueue(readQueue().filter(entry => entry.recordKey !== item.recordKey))
        markAcknowledged(item.recordKey)
      }
      catch {
        bumpRetry(item.recordKey)
        break
      }
    }

    if (readQueue().some(item => Number(item.nextRetryAt || 0) <= Date.now())) {
      scheduleFlush(250)
    }
    return true
  })().finally(() => {
    flushPromise = null
  })

  return flushPromise
}

function buildRecordKey(parts = []) {
  return parts
    .map(value => normalizeText(value, 255).toLowerCase().replace(/[^a-z0-9:_-]+/g, '_'))
    .filter(Boolean)
    .join(':')
    .slice(0, 100)
}

function reportWebOpenInternal() {
  if (!isClientRuntime())
    return false
  const site = getSiteContext()
  const recordKey = buildRecordKey([
    'web',
    'open',
    site.domain,
    getSessionId(),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ])
  const payload = buildBasePayload({
    eventName: 'web_open',
    eventModule: 'lifecycle',
    eventSource: 'web_boot',
    recordKey,
  })
  payload.itemType = 'web_lifecycle'
  payload.itemName = 'Web open'
  payload.targetRoute = payload.pageRoute
  payload.payload = {
    ...payload.payload,
    trigger: 'web_boot',
  }
  return enqueuePayload(payload)
}

export function reportWebOpen() {
  return runSafely(reportWebOpenInternal, false)
}

function reportBlockedOrderSuccessInternal(order = {}) {
  if (!isClientRuntime())
    return false

  const site = getSiteContext()
  const orderId = firstNonEmpty(order.orderId, order.dealerOrderPrimaryId)
  const rejectedOrderCode = firstNonEmpty(order.orderCode, order.orderNumber)
  const payOrderCode = firstNonEmpty(order.payOrderCode, order.payOrderNo)
  const diagnosticIdentity = firstNonEmpty(orderId, payOrderCode, rejectedOrderCode, 'unknown_order')
  const recordKey = buildRecordKey([
    'web', 'order_success_invalid_order_code', site.domain, diagnosticIdentity,
  ])
  const items = (Array.isArray(order.items) ? order.items : [])
    .slice(0, 100)
    .map(item => ({
      skuId: firstNonEmpty(item.skuId, item.offerId, item.id, item.content_id),
      spuId: firstNonEmpty(item.spuId, item.spItemSpuId),
      title: firstNonEmpty(item.title, item.subject, item.spuName, item.dealerItemSpuTitle, item.content_name, item.name),
      quantity: Number(item.buyAmount || item.quantity || 1),
      price: Number(item.price || item.salePrice || item.unitPrice || 0),
    }))
    .filter(item => item.quantity > 0 && (item.skuId || item.spuId || item.title))
  const amount = Number(order.amount ?? order.totalAmount ?? order.value)
  const payload = buildBasePayload({
    eventName: 'order_success_invalid_order_code',
    eventModule: 'order',
    eventSource: 'payment_result_guard',
    recordKey,
  })

  payload.eventStatus = 'skipped'
  payload.itemType = 'order'
  payload.itemName = `Blocked order success without SO order code ${rejectedOrderCode || diagnosticIdentity}`.slice(0, 255)
  payload.itemPrice = Number.isFinite(amount) ? amount : undefined
  payload.orderId = orderId || undefined
  payload.orderCode = rejectedOrderCode || undefined
  payload.payOrderCode = payOrderCode || undefined
  payload.targetRoute = '/payment/successful'
  payload.payload = {
    ...payload.payload,
    orderId,
    rejectedOrderCode,
    payOrderCode,
    amount: Number.isFinite(amount) ? amount : undefined,
    currency: firstNonEmpty(order.currency, 'TWD'),
    source: firstNonEmpty(order.source, 'order_success_guard'),
    purchaseSkipped: true,
    purchaseSkipReason: 'order_code_not_so_prefix',
    items,
  }
  payload.tags = {
    ...payload.tags,
    rejectedOrderCode,
    purchaseSkipReason: 'order_code_not_so_prefix',
  }
  return enqueuePayload(payload)
}

export function reportWebOrderSuccessBlocked(order = {}) {
  return runSafely(() => reportBlockedOrderSuccessInternal(order), false)
}

function reportWebOrderSuccessInternal(order = {}) {
  if (!isClientRuntime())
    return false

  const site = getSiteContext()
  const orderId = firstNonEmpty(order.orderId, order.dealerOrderPrimaryId)
  const orderCode = resolveOrderSuccessCode(order)
  const payOrderCode = firstNonEmpty(order.payOrderCode, order.payOrderNo)

  if (!orderCode) {
    reportBlockedOrderSuccessInternal(order)
    return false
  }

  const items = (Array.isArray(order.items) ? order.items : [])
    .slice(0, 100)
    .map(item => ({
      skuId: firstNonEmpty(item.skuId, item.offerId, item.id),
      spuId: firstNonEmpty(item.spuId, item.spItemSpuId),
      title: firstNonEmpty(item.title, item.subject, item.spuName, item.dealerItemSpuTitle),
      quantity: Number(item.buyAmount || item.quantity || 1),
      price: Number(item.price || item.salePrice || item.unitPrice || 0),
    }))
    .filter(item => item.quantity > 0 && (item.skuId || item.spuId || item.title))
  const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
  const amount = Number(order.amount ?? order.totalAmount ?? 0)
  const primaryOrderKey = firstNonEmpty(payOrderCode, orderCode)
  if (!primaryOrderKey || !items.length || totalQuantity <= 0 || !Number.isFinite(amount) || amount < 0)
    return false

  // A zero-value success is diagnostic, not a Purchase conversion. Its own
  // dedup key lets the same order report normally if its amount is filled later.
  const isZeroAmount = amount === 0
  const eventName = isZeroAmount ? 'order_success_zero_amount' : 'order_success'
  const pendingItems = readQueue()
  // 同一交易可能先只有订单号、后补支付单号。关联两者并复用原键，避免编号切换重复上报。
  // 排队中的记录也参与匹配，但只有服务端确认成功才进入 ACK，网络失败仍按原键重试。
  const recordKey = resolveOrderTrackingKey({
    scope: JSON.stringify([eventName, site.domain, site.shopId]),
    aliases: getOrderTrackingAliases({ orderCode, payOrderCode }),
    candidateKeys: [payOrderCode, orderCode].filter(Boolean)
      .map(key => buildRecordKey(['web', eventName, site.domain, key])),
    knownKeys: [...readAckKeys(), ...pendingItems.map(item => item.recordKey)],
    // 升级前的待重试事件没有关联表，从原请求里的两个编号恢复关联。
    knownIdentities: pendingItems.map(({ recordKey, payload: pending }) => ({
      scope: JSON.stringify([pending.eventName, pending.payload?.domain, pending.payload?.shopId]),
      recordKey,
      aliases: getOrderTrackingAliases(pending),
    })),
  })

  const payload = buildBasePayload({
    eventName,
    eventModule: 'order',
    eventSource: 'payment_result',
    recordKey,
  })
  const attributionContext = payload.payload?.attribution || {}
  const resolvedTouch = attributionContext.currentTouch?.isAttributed
    ? attributionContext.currentTouch
    : (attributionContext.lastTouch || {})
  const attributionDiagnostics = buildAttributionDiagnostics({
    context: attributionContext,
    touch: resolvedTouch,
    purchaseEventId: firstNonEmpty(order.purchaseEventId, order.eventId),
    metaTrackingSnapshot: order.metaTrackingSnapshot,
  })

  payload.itemType = 'order'
  payload.itemName = `Web order success${isZeroAmount ? ' with zero amount' : ''} ${primaryOrderKey}`.slice(0, 255)
  payload.itemPrice = Number.isFinite(amount) ? amount : undefined
  payload.orderId = orderId || undefined
  payload.orderCode = orderCode || undefined
  payload.payOrderCode = payOrderCode || undefined
  payload.targetRoute = '/payment/successful'
  payload.payload = {
    ...payload.payload,
    orderId,
    orderCode,
    payOrderCode,
    amount: Number.isFinite(amount) ? amount : 0,
    currency: firstNonEmpty(order.currency, 'TWD'),
    totalQuantity,
    payType: firstNonEmpty(order.payType, order.paymentType),
    status: firstNonEmpty(order.status, 'success'),
    source: firstNonEmpty(order.source, 'payment_successful_page'),
    ...(isZeroAmount ? { purchaseSkipped: true, purchaseSkipReason: 'zero_amount' } : {}),
    attributionDiagnostics,
    items,
  }
  payload.tags = {
    ...payload.tags,
    purchaseEventId: attributionDiagnostics.purchaseEventId || '',
    diagnosticPageFbclid: attributionDiagnostics.pageFbclid || '',
    diagnosticLocalStorageFbclid: attributionDiagnostics.localStorageFbclid || '',
    diagnosticMetaFbc: attributionDiagnostics.metaFbc || '',
    diagnosticMetaFbp: attributionDiagnostics.metaFbp || '',
    diagnosticMetaFbclid: attributionDiagnostics.metaFbclid || '',
    diagnosticMetaFbcStatus: attributionDiagnostics.metaFbcStatus || '',
    diagnosticMetaFbcMatchesPage: String(Boolean(attributionDiagnostics.consistency?.metaFbcMatchesPage)),
    diagnosticMetaFbcMatchesResolved: String(Boolean(attributionDiagnostics.consistency?.metaFbcMatchesResolved)),
  }
  return enqueuePayload(payload)
}

export function reportWebOrderSuccess(order = {}) {
  return runSafely(() => reportWebOrderSuccessInternal(order), false)
}

function reportWebFunnelEventInternal(eventName, details = {}) {
  if (!isClientRuntime())
    return false

  const config = Object.prototype.hasOwnProperty.call(FUNNEL_EVENTS, eventName)
    ? FUNNEL_EVENTS[eventName]
    : null
  if (!config)
    return false

  const site = getSiteContext()
  const eventId = normalizeText(details.eventId, 100)
  const recordKey = buildRecordKey([
    'web', eventName, site.domain, eventId || randomId('event'),
  ])
  const contents = (Array.isArray(details.contents) ? details.contents : [])
    .slice(0, 50)
    .map(item => ({
      itemId: firstNonEmpty(item.itemId, item.content_id, item.id),
      itemName: firstNonEmpty(item.itemName, item.content_name, item.name),
    }))
    .filter(item => item.itemId || item.itemName)
  const itemId = firstNonEmpty(details.itemId, contents[0]?.itemId)
  const itemName = firstNonEmpty(details.itemName, contents[0]?.itemName)
  const numericValue = Number(details.value)
  const payload = buildBasePayload({
    eventName,
    eventModule: config.module,
    eventSource: config.source,
    recordKey,
  })

  if (eventName === 'login_success')
    payload.isLogin = true

  payload.itemType = config.itemType
  payload.itemId = itemId || recordKey
  payload.itemName = itemName || eventName
  payload.targetRoute = payload.pageRoute
  if (details.value !== undefined && Number.isFinite(numericValue))
    payload.itemPrice = numericValue
  payload.payload = {
    ...payload.payload,
    eventId: eventId || undefined,
    method: normalizeText(details.method, 50) || undefined,
    surface: normalizeText(details.surface, 50) || undefined,
    itemId: itemId || undefined,
    itemName: itemName || undefined,
    value: details.value !== undefined && Number.isFinite(numericValue) ? numericValue : undefined,
    currency: normalizeText(details.currency, 10) || undefined,
    itemCount: Number.isFinite(Number(details.itemCount)) ? Number(details.itemCount) : undefined,
    contents: contents.length ? contents : undefined,
  }

  return enqueuePayload(payload)
}

export function reportWebFunnelEvent(eventName, details = {}) {
  return runSafely(() => reportWebFunnelEventInternal(eventName, details), false)
}

const SUCCESS_APP_DOWNLOAD_POPUP_ACTIONS = new Set([
  'close',
  'download_click',
  'show',
])

function reportSuccessAppDownloadPopupEventInternal(event = {}) {
  if (!isClientRuntime())
    return false

  const action = normalizeText(event.action, 50).toLowerCase()
  if (!SUCCESS_APP_DOWNLOAD_POPUP_ACTIONS.has(action))
    return false

  const orderNumber = firstNonEmpty(event.orderNumber, event.orderCode, event.orderId)
  const recordKey = buildRecordKey([
    'web',
    'success_app_popup',
    randomId('event'),
    action,
  ])
  const payload = buildBasePayload({
    eventName: `success_app_download_popup_${action}`,
    eventModule: 'promotion',
    eventSource: 'payment_success',
    recordKey,
  })

  payload.itemType = 'app_download_popup'
  payload.itemName = 'Success App Download Popup'
  payload.orderCode = orderNumber || undefined
  payload.targetRoute = '/payment/successful'
  payload.payload = {
    ...payload.payload,
    action,
    orderNumber,
    couponValue: Number(event.couponValue || 0),
    minimumSpend: Number(event.minimumSpend || 0),
    platform: normalizeText(event.platform, 50),
    closeMethod: normalizeText(event.closeMethod, 50),
    interactionMode: normalizeText(event.interactionMode, 50),
    hasDownloadUrl: event.hasDownloadUrl === undefined
      ? undefined
      : Boolean(event.hasDownloadUrl),
  }
  payload.tags = {
    ...payload.tags,
    action,
    closeMethod: normalizeText(event.closeMethod, 50),
    interactionMode: normalizeText(event.interactionMode, 50),
  }

  return enqueuePayload(payload)
}

export function reportSuccessAppDownloadPopupEvent(event = {}) {
  return runSafely(() => reportSuccessAppDownloadPopupEventInternal(event), false)
}

function setupRuntimeListeners() {
  window.addEventListener('online', () => scheduleFlush(100))
  window.addEventListener('pagehide', () => {
    void flushClientEventLogQueue({ keepalive: true })
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushClientEventLogQueue({ keepalive: true })
    }
  })
}

export function initializeClientEventLog(nuxtApp) {
  if (initialized || !isClientRuntime())
    return
  initialized = true
  runSafely(() => captureWebAttribution())
  runSafely(() => setupRuntimeListeners())
  runSafely(() => nuxtApp?.$router?.afterEach?.(() => {
    runSafely(() => captureWebAttribution())
  }))
  window.setTimeout(() => {
    runSafely(() => reportWebOpen())
    runSafely(() => void flushClientEventLogQueue())
  })
}
